import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Label, TextArea } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import { hubModalBodyScrollClassName, hubModalShellClassName } from "@/lib/hubLayoutClassNames";

import {
  correctiveActionSourceLabel,
  qualityEventSourceOptions,
  type CorrectiveActionSource,
} from "./correctiveActionsApi";
import { QualityCustomerPicker } from "./QualityCustomerPicker";

type SupplierSearchRow = {
  id: number;
  mv_supplier_name: string;
};

export function CreateQualityEventModal(props: {
  open: boolean;
  pending: boolean;
  onClose: () => void;
  onCreate: (input: {
    sourceCategory: CorrectiveActionSource;
    summary: string | null;
    supplierId: number | null;
    customerFsAccountId: number | null;
  }) => void;
}) {
  const { open, pending, onClose, onCreate } = props;
  const [sourceCategory, setSourceCategory] = useState<CorrectiveActionSource>("qc_incident");
  const [summary, setSummary] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierLabel, setSupplierLabel] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState("");
  const [customerFsAccountId, setCustomerFsAccountId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setSourceCategory("qc_incident");
    setSummary("");
    setSupplierId("");
    setSupplierLabel("");
    setSupplierSearch("");
    setDebouncedSupplierSearch("");
    setCustomerFsAccountId(null);
  }, [open]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSupplierSearch(supplierSearch), 250);
    return () => window.clearTimeout(handle);
  }, [supplierSearch]);

  const supplierQuery = useQuery({
    queryKey: ["quality", "event-create", "suppliers", debouncedSupplierSearch],
    queryFn: ({ signal }) =>
      apiFetch<{ rows: SupplierSearchRow[] }>("/api/mrp/suppliers/quick-search", {
        query: { q: debouncedSupplierSearch, limit: "12" },
        signal,
      }),
    enabled: open && sourceCategory === "supplier",
  });

  const supplierOptions = useMemo((): SearchableSelectOption[] => {
    const rows = supplierQuery.data?.rows ?? [];
    const options = rows.map((row) => ({
      value: String(row.id),
      label: row.mv_supplier_name || `Supplier ${row.id}`,
    }));
    if (supplierId !== "" && !options.some((option) => option.value === supplierId)) {
      options.unshift({ value: supplierId, label: supplierLabel || `Supplier ${supplierId}` });
    }
    return options;
  }, [supplierId, supplierLabel, supplierQuery.data?.rows]);

  if (!open) return null;

  const needsSupplier = sourceCategory === "supplier";
  const needsCustomer = sourceCategory === "customer_complaint";
  const canSubmit = !pending && (!needsSupplier || supplierId !== "") && (!needsCustomer || customerFsAccountId != null);

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className={hubModalShellClassName("form")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-quality-event-title"
      >
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          <h2 id="create-quality-event-title" className="text-base font-semibold text-slate-900">
            New quality event
          </h2>
          <HoverHintWithGlyph hoverHint="Creates an open quality event. Add notes, NCRs, and a case from the event page after it is saved." />
        </div>
        <form
          className={`${hubModalBodyScrollClassName()} space-y-3 px-4 py-4`}
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            onCreate({
              sourceCategory,
              summary: summary.trim() === "" ? null : summary.trim(),
              supplierId: needsSupplier ? Number(supplierId) : null,
              customerFsAccountId: needsCustomer ? customerFsAccountId : null,
            });
          }}
        >
          <div>
            <Label htmlFor="create-quality-event-source">Source</Label>
            <select
              id="create-quality-event-source"
              className="mt-1.5 flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
              value={sourceCategory}
              onChange={(event) => setSourceCategory(event.target.value as CorrectiveActionSource)}
            >
              {qualityEventSourceOptions().map((source) => (
                <option key={source} value={source}>
                  {correctiveActionSourceLabel(source)}
                </option>
              ))}
            </select>
          </div>
          {needsSupplier ? (
            <div>
              <Label htmlFor="create-quality-event-supplier">Supplier</Label>
              <div className="mt-1.5">
                <SearchableSelect
                  id="create-quality-event-supplier"
                  value={supplierId}
                  options={supplierOptions}
                  searchValue={supplierSearch}
                  onSearchChange={setSupplierSearch}
                  isLoading={supplierQuery.isLoading}
                  placeholder="Choose a supplier"
                  searchPlaceholder="Search suppliers"
                  emptyMessage="No suppliers match that search."
                  onValueChange={(option) => {
                    setSupplierId(option?.value ?? "");
                    setSupplierLabel(option?.label ?? "");
                  }}
                />
              </div>
            </div>
          ) : null}
          {needsCustomer ? (
            <div>
              <Label htmlFor="create-quality-event-customer">Customer</Label>
              <div className="mt-1.5">
                <QualityCustomerPicker
                  id="create-quality-event-customer"
                  value={customerFsAccountId}
                  onChange={setCustomerFsAccountId}
                />
              </div>
            </div>
          ) : null}
          <div>
            <Label htmlFor="create-quality-event-summary">Summary</Label>
            <TextArea
              id="create-quality-event-summary"
              value={summary}
              rows={3}
              onChange={(event) => setSummary(event.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              Create event
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
